import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { CookieService } from "ngx-cookie-service";
import { environment } from '../../environments/environments';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '../models/user.model';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { ProfileCacheService } from '../core/services/profile-cache.service';

/** Ruta a la que se sale cuando termina la sesión (logout o 401). */
const LOGIN_PATH = '/login';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    // private endpoint1 = environment.apiUrl + '/v1';
    private endpoint1 = environment.apiUrl + '/v1';
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;

    constructor(
        private http: HttpClient,
        private cookieService: CookieService,
        private router: Router,
        private profileCacheService: ProfileCacheService
    ) {
        this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser') || '{}'));
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    /**
     * Token, estación y CRE se leen de localStorage en cada acceso.
     *
     * Antes se capturaban una sola vez en el constructor: como el servicio es un
     * singleton que nace antes del login, se quedaban en null para toda la vida
     * del documento y el único remedio era un `window.location.reload()` después
     * de autenticarse. Con getters, cualquier consumidor (interceptor, servicios,
     * componentes) ve siempre la sesión vigente sin recargar.
     */
    public get token(): string | null {
        return localStorage.getItem('token');
    }

    public get idStation(): string | null {
        return localStorage.getItem('idStation');
    }

    public get creId(): string | null {
        return localStorage.getItem('creId');
    }

    login(email: string, password: string) {

        const data = { 'email': email, 'password': password };

        return this.http.post<any>(`${this.endpoint1}/crm/users/login`, data);
    }

    /**
     * Persiste la sesión que devolvió `login` y la publica en memoria.
     *
     * Es el único lugar que escribe las llaves de sesión: así el login puede
     * navegar con el router (sin recarga) y el resto de la app encuentra token,
     * estación, CRE y rol ya listos cuando se construye.
     */
    setSession(data: any, email: string, password: string): void {
        const user = data?.user ?? {};
        const firstStation = user.stations?.[0] ?? {};

        data.authdata = window.btoa(email + ':' + password);

        localStorage.setItem('currentUser', JSON.stringify(data));
        localStorage.setItem('token', user.token ?? '');
        localStorage.setItem('idStation', firstStation._id ?? '');
        localStorage.setItem('creId', firstStation.cre ?? '');
        localStorage.setItem('rol', user.rol ?? '');

        this.currentUserSubject.next(data);
    }

    loginPrices(user: string, password: string) {

        const data = { 'username': user, 'password': password.toLowerCase() };

        return this.http.post(`${this.endpoint1}/prices/users/login`, data)
            .pipe(
                map((x: any) => { return x['username'] }),
                shareReplay(1),
                catchError(this.processError)
            );
    }

    setToken(token) {
        this.cookieService.set("tokenPrices", token);
    }

    getToken() {
        return this.cookieService.get("tokenPrices");
    }

    setStation(station) {
        this.cookieService.set("idStationPrices", station);
    }

    getStation() {
        return this.cookieService.get("idStationPrices");
    }

    signup(name: string, email: string, cre: string) {

        const data = { 'name': name, 'email': email, 'cre': cre };

        return this.http.post<any>(`${this.endpoint1}/crm/users/signup`, data)
            .pipe(map(user => {
                // console.log('entro ', user);
            })
            );
    }

    /**
     * Cierra la sesión del usuario y limpia todos los cachés en memoria y en
     * localStorage. No navega: ver `leaveToLogin`.
     */
    logout() {
        // Limpiar el caché del perfil
        this.profileCacheService.clearCache();

        // Limpiar localStorage
        localStorage.clear();

        // Resetear el subject del usuario actual
        this.currentUserSubject.next(null);
    }

    /**
     * Cierra la sesión y sale al login con una carga completa del documento.
     *
     * Es a propósito una carga de documento y no `router.navigate`: muchos
     * servicios singleton (GasprecioService, StationService, SharedService…)
     * capturan estación, rol y grupo en su constructor y memoizan respuestas.
     * Tirar el documento garantiza que el siguiente login arranque limpio, y es
     * lo que permite que el login en sí ya no necesite recargar la página.
     */
    leaveToLogin(): void {
        this.logout();
        window.location.assign(LOGIN_PATH);
    }

    logoutPrices() {
        localStorage.removeItem('idStationPrices');
        localStorage.removeItem('tokenPrices');
        localStorage.removeItem('lastOrientationAlertTime');
        this.cookieService.delete("tokenPrices");
        this.cookieService.delete("idStationPrices");
        this.router.navigateByUrl('precios/login');
    }

    sendRecoverPassword(email: any) {

        const data = { 'email': email };

        return new Promise(resolve => {
            this.http.post(`${this.endpoint1}/crm/users/auth/forgot_password`, data)
                .subscribe(async resp => {
                    resolve(true);
                });
        });
    }

    setPassword(password: any, token: any) {

        password.token = token;

        return new Promise(resolve => {
            this.http.put<any>(`${this.endpoint1}/crm/users/password`, password)
                .subscribe(user => {
                    resolve(true);
                });
        });
    }

    verifyAccount(token: any) {

        const data = '';

        return new Promise(() => {
            this.http.put<any>(`${this.endpoint1}/app/users/verify_account`, data, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }).subscribe(async resp => {
                // console.log(resp);
                return resp;
            });
        });
    }

    processError(err: any) {
        let message = '';
        if (err.error instanceof ErrorEvent) {
            message = err.error.message;
        } else {
            message = `Error Code: ${err.status}\nMessage: ${err.message}`;
        }
        return throwError(() => {
            message;
        });
    }

    showLoader(title: string, text: string) {
        Swal.fire({
            title: title,
            text: text,
            allowEscapeKey: false,
            allowEnterKey: false,
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading()
            }
        });
    }

}
